import { COMPANY_ADMIN_ROLE } from 'constants/companyRoles';
import { isoDay } from 'helper/timeLabels';
import { ask, bigAccountsById, forget, realCompanies } from './crmRequests';

/**
 * The api of the record pages (a company, a group, a contact). Each read
 * gathers the real data of Jobbiz (the company, its members, its missions,
 * its invoices, its group) and the sales side the api holds.
 *
 * A company's record reads its activity from the api (POST
 * admin/crm/company/activity { companyId } → { tasks, appointments, calls,
 * notes, clientEvents, demoRequests }, in the shapes of a group's), its results (POST
 * admin/crm/company/results { companyId }, `record.results`, null when the
 * read fails) and the CRM's team. Its CRM state (stage, commercial, size,
 * switchboard…) is not here: the page reads it from admin/crm/company/crm.
 * A company deleted answers errorCode COMPANY_NOT_FOUND.
 *
 * A group's record reads the api (POST admin/crm/group { groupId }): the
 * group { id, name, phone, email }, its companies { id, name, department,
 * stage, missionCount, lastExchangeAt, members }, each member { userId,
 * firstname, lastname, email, phone, position, role, account }, its contacts
 * { id, firstname, lastname, email, phone, position } and `access: {
 * canEditContacts }`. Its activity reads the api too (POST
 * admin/crm/group/activity { groupId } → { tasks, appointments, calls, notes,
 * clientEvents }), each entry filed under its company when it is one of the
 * group's, else under the group (groupActivitySources). Its missions and its
 * invoices are still read company by company. Its phone and e-mail are
 * written by crmApi.js (saveGroupInfo), its contacts too (saveGroupContact,
 * deleteGroupContact). A group gone answers errorCode GROUP_NOT_FOUND. A
 * note is written by the api too
 * (crmApi.js: addRecordNote, updateRecordNote, deleteRecordNote), and the
 * notes of a contact's record are read from it (POST admin/crm/notes
 * { contactId }: the account's id, else 'name:<name>'), so that a note linked
 * to several records shows on each.
 * The position of a member with an account is the api's (memberPositions of
 * POST admin/crm/company/crm), read by a contact's page (a group's has it in
 * its members); the
 * company's page reads it with the rest of company/crm.
 *
 * A contact's record reads the api (POST admin/crm/contact/record
 * { contactId } | { email } → { person, companies, activity }): the person,
 * every company and group they are at (their role, a place deactivated), and
 * their activity in all of them (contactRecordOf). A contact unknown answers
 * errorCode CONTACT_NOT_FOUND.
 *
 * The e-mails of a company's record, and of a group's and each of its
 * companies', are read from the api (POST admin/crm/record/emails { companyId }
 * | { groupId } → { data, intercom }): the sent ones and those Intercom holds
 * from the contacts; `history.intercom` is 'unavailable' when Intercom did not
 * answer. An e-mail is sent by crmApi.js (sendRecordEmail).
 *
 * The Wildix calls of a company's record, and those of a group's own
 * contacts (POST admin/crm/record/calls, readPhoneCalls), join the calls
 * logged in the CRM; a note may be written on one (`phoneCallId`).
 *
 * A company made a group (makeCompanyGroup): POST admin/crm/group/from-company
 * { companyId, name } → { groupId }. A refusal carries an errorCode (COMPANY_NOT_FOUND,
 * COMPANY_ALREADY_IN_GROUP, GROUP_NAME_TAKEN, GROUP_CONTACTS_FULL, INVALID_GROUP_NAME,
 * FORBIDDEN) that the form translates.
 *
 * getGroupInvoiceTotals reads POST admin/crm/group/invoices/totals { groupId }
 * (computed by the api), listGroupCandidates reads the
 * companies list without those already in a group, addCompanyToGroup calls
 * POST admin/linkCompanyToBigAccount { idCompany, idBigAccount }.
 */

const fullName = (person) => [person.firstname, person.lastname].filter(Boolean).join(' ');

// Its people: the members of the company (their account and their access),
// each with their position (`positions`, by account). Their lead status is the
// api's (memberLeadStatuses of company/crm), put in by the page.
const membersOf = (company, positions = {}) =>
  (company?.employees ?? [])
    .filter((entry) => entry?.user?._id)
    .map((entry) => {
      const id = String(entry.user._id);
      return {
        id,
        firstname: entry.user.firstname ?? '',
        lastname: entry.user.lastname ?? '',
        email: entry.user.email ?? '',
        phone: entry.user.phone ?? '',
        role: entry.role,
        account: entry.user.validation === 0 ? 'pending' : 'active',
        position: positions[id] ?? '',
      };
    });

// Its missions, to come, under way or over, with their temp worker.
const missionState = (mission, now) => {
  const start = new Date(mission.duration?.datestart);
  const end = new Date(mission.duration?.dateend ?? mission.duration?.datestart);
  end.setHours(23, 59, 59, 999);
  if (start > now) return 'upcoming';
  return end < now ? 'done' : 'current';
};
const missionsOf = ({ missions, tempWorkers }, now, companyName) => ({
  missions: missions.map((mission) => ({
    id: String(mission._id),
    number: mission.id_mission,
    title: mission.title,
    startsOn: mission.duration?.datestart,
    endsOn: mission.duration?.dateend ?? mission.duration?.datestart,
    state: missionState(mission, now),
    interrupted: Boolean(mission.interruption),
    worker: mission.tempWorker ? fullName(mission.tempWorker) : '',
    companyName,
  })),
  tempWorkers: tempWorkers.map((worker) => ({
    id: String(worker._id),
    name: fullName(worker),
    missions: worker.missionsCount ?? 0,
    positive: worker.ratings?.positive ?? 0,
    negative: worker.ratings?.negative ?? 0,
  })),
});

// A contact of a group, as the api answers it. A group has no account in
// Jobbiz; its lead status is the api's. `userId` is set when the contact has one.
const groupContactOf = (contact) => ({
  id: String(contact.id),
  firstname: contact.firstname ?? '',
  lastname: contact.lastname ?? '',
  email: contact.email ?? '',
  phone: contact.phone ?? '',
  position: contact.position ?? '',
  account: null,
  leadStatus: contact.leadStatus ?? '',
  ...(contact.userId && { userId: String(contact.userId) }),
});

const readGroup = (groupId) => ask('getCrmGroup', { groupId: String(groupId) });
const readGroupActivity = (groupId) => ask('getCrmGroupActivity', { groupId: String(groupId) });

// The e-mails of one record ({ companyId } or { groupId }), sent and received.
const readEmails = (key) =>
  ask('getCrmRecordEmails', key).then((answer) => ({ data: answer?.data ?? [], intercom: answer?.intercom ?? 'ok' }));

/**
 * The Wildix calls of one record (POST admin/crm/record/calls { companyId } |
 * { groupId }), matched by its phone numbers. A failed read leaves the page
 * without them rather than without the page.
 */
const readPhoneCalls = (key) =>
  ask('getCrmRecordCalls', key)
    .then((answer) => (Array.isArray(answer?.data) ? answer.data : []))
    .catch(() => [])
    .then((calls) => calls.map((call) => ({ ...call, wildix: true })));
const latestFirst = (calls) => [...calls].sort((a, b) => new Date(b.at) - new Date(a.at));

// The companies an entry of the activity names: its own, then those of its links.
const linkedCompanies = (links) =>
  (links ?? []).map((link) => (link.type === 'company' ? link.id : link.type === 'contact' ? link.companyId : null));

// The person a call, an e-mail or an appointment is about (`contactRef`,
// JBWEB-1427), out of a record's contact: a member with an account carries
// `userId`, a contact without one `contactId` (its sub-document) and its
// address. A contact known by its name only has none.
export const contactRefOf = (contact) => {
  if (contact?.userId) return { userId: contact.userId };
  if (contact?.contactId) return { contactId: contact.contactId, ...(contact.email ? { email: contact.email } : {}) };
  return null;
};

// What a body gets: nothing without a person, so that it stays the one the api
// had before contactRef.
export const contactRefField = (contactRef) => (contactRef ? { contactRef } : {});

// A call logged in the CRM (crmCall), in the shape buildActivity reads;
// `noteLink`: the record a note written on it is linked to.
const crmCallOf = (call, noteLink) => ({
  id: call.id,
  at: call.at,
  authorId: call.author?.id ?? null,
  contactName: call.contactName ?? '',
  result: call.result,
  note: call.note ?? '',
  noteLink,
});

// An appointment of the api, as an event of the activity.
const appointmentEventOf = (appointment) => ({
  id: appointment.id,
  startsAt: appointment.startsAt,
  title: appointment.title ?? '',
  contactName: '',
  mode: '',
  outcome: '',
  description: '',
  status: appointment.status,
});

/**
 * The activity of a company (POST admin/crm/company/activity) in the shape
 * buildActivity reads: its calls logged in the CRM, its appointments, its
 * tasks, its notes (`at`: their creation), what the client did in Jobbiz and
 * the demo requests of the site.
 * `noteLink`: the company, which a note on one of its calls is linked to.
 * `people`: the authors and owners the api names, for their names.
 */
export const companyActivitySources = (activity, noteLink) => {
  const people = [
    ...(activity?.calls ?? []).map((call) => call.author),
    ...(activity?.appointments ?? []).map((appointment) => appointment.owner),
    ...(activity?.notes ?? []).map((note) => note.author),
  ].filter(Boolean);
  return {
    history: {
      calls: (activity?.calls ?? []).map((call) => crmCallOf(call, noteLink)),
      events: (activity?.appointments ?? []).map(appointmentEventOf),
      tasks: activity?.tasks ?? [],
      notes: (activity?.notes ?? []).map((note) => ({ ...note, at: note.createdAt })),
      clientEvents: activity?.clientEvents ?? [],
      // The demo requests of the site (JBWEB-1420): [{ id, at, outcome }].
      demoRequests: activity?.demoRequests ?? [],
      // Every sales rep's pending call-list entries: [{ id, ownerId, kind, dueAt }].
      callbacks: activity?.callbacks ?? [],
    },
    people,
  };
};

/**
 * The activity of a group (POST admin/crm/group/activity) in the shape
 * buildActivity reads (activityItems.js), filed by record: a map from the
 * group's id or one of its companies' to { calls, events, tasks, notes }. An
 * entry goes to its company when it is one of the group's, else to the group;
 * a note written on a call goes where the call is, so that it shows under it,
 * once. A call carries `noteLink`, the record a note on it is linked to.
 * `people`: the authors and owners the api names, for their names.
 */
export const groupActivitySources = (activity, { group, companies }) => {
  const names = new Map(companies.map((company) => [company.id, company.name]));
  const bySource = new Map(
    [group.id, ...companies.map((company) => company.id)].map((id) => [id, { calls: [], events: [], tasks: [], notes: [], clientEvents: [] }]),
  );
  const sourceOf = (ids) => ids.find((id) => id && names.has(String(id))) ?? group.id;
  const people = [];
  const callSources = new Map();
  (activity?.calls ?? []).forEach((call) => {
    const source = sourceOf([call.companyId]);
    callSources.set(call.id, source);
    if (call.author) people.push(call.author);
    bySource
      .get(source)
      .calls.push(crmCallOf(call, source === group.id ? { type: 'group', id: group.id, label: group.name } : { type: 'company', id: source, label: names.get(source) }));
  });
  (activity?.appointments ?? []).forEach((appointment) => {
    if (appointment.owner) people.push(appointment.owner);
    bySource.get(sourceOf([appointment.companyId])).events.push(appointmentEventOf(appointment));
  });
  (activity?.tasks ?? []).forEach((task) => {
    bySource.get(sourceOf([task.companyId, ...linkedCompanies(task.links)])).tasks.push(task);
  });
  (activity?.notes ?? []).forEach((note) => {
    if (note.author) people.push(note.author);
    const source = callSources.get(note.callId) ?? sourceOf(linkedCompanies(note.links));
    bySource.get(source).notes.push({ ...note, at: note.createdAt });
  });
  (activity?.clientEvents ?? []).forEach((event) => {
    bySource.get(sourceOf([event.companyId])).clientEvents.push(event);
  });
  return { bySource, people };
};

// The people the page names, each once, the first list winning: the CRM's
// team, then the authors and owners of the activity.
const peopleOf = (...lists) => {
  const seen = new Map();
  lists.flat().forEach((person) => person?.id && !seen.has(String(person.id)) && seen.set(String(person.id), { ...person, id: String(person.id) }));
  return [...seen.values()];
};

/**
 * Everything a company's page shows but its CRM state (admin/crm/company/crm,
 * read by the page). `summary`: its line of the companies list (its creation,
 * its missions), which getCompanyInfo does not return. The members' positions
 * are read by the page with the rest of company/crm. `withResults`: its
 * results over 12 months.
 */
const loadCompanyRecord = async (
  payload,
  { withResults = true } = {},
) => {
  const now = new Date();
  const id = String(payload.companyId);
  const [info, missionData, list, groups, activity, results, emails, phoneCalls, crmTeam] = await Promise.all([
    ask('getCompanyInfo', { idCompany: id }, `crm:info:${id}`).then((data) => data?.company ?? null),
    ask('getCompanyMissionsAndTempWorkers', { idCompany: id }, `crm:missions:${id}`).then((data) => ({
      missions: data?.missions ?? [],
      tempWorkers: data?.tempWorkers ?? [],
    })),
    realCompanies().catch(() => []),
    bigAccountsById().catch(() => ({})),
    // A refused activity (a company deleted: COMPANY_NOT_FOUND) refuses the record.
    ask('getCrmCompanyActivity', { companyId: id }),
    // Failed results leave the record without its « Résultats » card.
    withResults ? ask('getCrmCompanyResults', { companyId: id }).catch(() => null) : Promise.resolve(null),
    readEmails({ companyId: id }).catch(() => ({ data: [], intercom: 'unavailable' })),
    readPhoneCalls({ companyId: id }),
    ask('getCrmTeam', {}).catch(() => []),
  ]);
  if (!info) throw new Error('Cette entreprise n’existe plus.');
  const name = info.name_commercial || info.reason_social || '';
  const noteLink = { type: 'company', id, label: name };
  const { history, people } = companyActivitySources(activity, noteLink);
  const summary = list.find((company) => String(company._id) === id) ?? null;
  const missions = missionsOf(missionData, now, name);
  const members = membersOf(info);
  const current = missions.missions.filter((mission) => mission.state === 'current');
  const groupContacts = groups[id]
    ? await readGroup(groups[id].id).then((answer) => (answer?.contacts ?? []).map((contact) => groupContactOf(contact)), () => [])
    : [];
  return {
    company: info,
    summary: summary && { createdAt: summary.createdAt, missionCount: summary.missionCount, department: summary.department },
    group: groups[id] ?? null,
    groupContacts,
    members,
    missions,
    kpis: {
      current: current.length,
      toFill: current.filter((mission) => !mission.worker).length,
      // Computed by the api for every CRM role; null when the results failed.
      invoices: results?.invoiceTotals ?? null,
    },
    history: {
      ...history,
      // Its Wildix calls join those logged in the CRM, by date; a note on one is linked to the company.
      calls: latestFirst([...history.calls, ...phoneCalls.map((call) => ({ ...call, noteLink }))]),
      emails: emails.data,
      intercom: emails.intercom,
    },
    results: results ?? null,
    team: peopleOf(Array.isArray(crmTeam) ? crmTeam : [], people),
  };
};

const loadGroupRecord = async (payload) => {
  const now = new Date();
  const [answer, activity, crmTeam, groupEmails, phoneCalls] = await Promise.all([
    readGroup(payload.groupId),
    readGroupActivity(payload.groupId),
    ask('getCrmTeam', {}).catch(() => []),
    readEmails({ groupId: payload.groupId }).catch(() => ({ data: [], intercom: 'unavailable' })),
    readPhoneCalls({ groupId: String(payload.groupId) }),
  ]);
  const group = {
    id: String(answer.group.id),
    name: answer.group.name ?? '',
    phone: answer.group.phone ?? '',
    email: answer.group.email ?? '',
  };
  const groupLink = { type: 'group', id: group.id, label: group.name };
  const { bySource, people } = groupActivitySources(activity, {
    group,
    companies: (answer.companies ?? []).map((company) => ({ id: String(company.id), name: company.name ?? '' })),
  });
  const companies = await Promise.all(
    (answer.companies ?? []).map(async (company) => {
      const id = String(company.id);
      const name = company.name ?? '';
      const [missionData, emails] = await Promise.all([
        ask('getCompanyMissionsAndTempWorkers', { idCompany: id }, `crm:missions:${id}`)
          .then((data) => ({ missions: data?.missions ?? [], tempWorkers: data?.tempWorkers ?? [] }))
          .catch(() => ({ missions: [], tempWorkers: [] })),
        // One company's failed read must not keep the whole group page from loading.
        readEmails({ companyId: id }).catch(() => ({ data: [], intercom: 'unavailable' })),
      ]);
      // Its active members, with their role there, their invitation and their lead status, as the api answers them.
      const members = (company.members ?? []).map((member) => ({
        id: String(member.userId),
        firstname: member.firstname ?? '',
        lastname: member.lastname ?? '',
        email: member.email ?? '',
        phone: member.phone ?? '',
        role: member.role ?? null,
        account: member.account ?? 'active',
        position: member.position ?? '',
        leadStatus: member.leadStatus ?? '',
      }));
      const missions = missionsOf(missionData, now, name);
      return {
        id,
        name,
        department: company.department ?? '',
        stage: company.stage ?? 'client',
        missionCount: company.missionCount ?? 0,
        lastExchangeAt: company.lastExchangeAt ?? null,
        members,
        missions,
        // What its members did in Jobbiz (group/activity), as on the company's own page.
        history: { ...bySource.get(id), emails: emails.data },
        intercom: emails.intercom,
      };
    }),
  );
  const contacts = (answer.contacts ?? []).map((contact) => groupContactOf(contact));
  // An e-mail one of its companies shows (the group's answer may hold its
  // companies' too) is not shown again under the group.
  const companyEmailIds = new Set(companies.flatMap((company) => company.history.emails.map((email) => email.id)));
  const ownEmails = groupEmails.data.filter((email) => !companyEmailIds.has(email.id));
  const intercom = [groupEmails, ...companies].some((one) => one.intercom === 'unavailable') ? 'unavailable' : 'ok';
  const current = companies.flatMap((company) => company.missions.missions.filter((mission) => mission.state === 'current'));
  return {
    group,
    access: { canEditContacts: Boolean(answer.access?.canEditContacts) },
    contacts,
    companies: companies.map(({ intercom: _intercom, ...company }) => company),
    // Its Wildix calls (its contacts' numbers) join the calls logged in the CRM.
    history: {
      ...bySource.get(group.id),
      calls: latestFirst([...bySource.get(group.id).calls, ...phoneCalls.map((call) => ({ ...call, noteLink: groupLink }))]),
      emails: ownEmails,
      intercom,
    },
    kpis: { current: current.length, toFill: current.filter((mission) => !mission.worker).length },
    team: peopleOf(Array.isArray(crmTeam) ? crmTeam : [], people),
  };
};

const contactNotFound = () =>
  Object.assign(new Error('CONTACT_NOT_FOUND'), { response: { status: 404, data: { errorCode: 'CONTACT_NOT_FOUND' } } });

// The record a page was opened from, when the person is not (or no longer)
// at it: its name only.
const ownerNameOf = ({ companyId, groupId }) =>
  groupId
    ? readGroup(groupId).then((answer) => answer?.group?.name ?? '', () => '')
    : ask('getCompanyInfo', { idCompany: String(companyId) }, `crm:info:${companyId}`).then(
        (data) => data?.company?.name_commercial || data?.company?.reason_social || '',
        () => '',
      );

// Whether a company keeps another administrator than `personId` once they
// leave it (useRemoveMember); a deactivated one counts for none.
const keepsAnotherAdmin = (companyId, personId) =>
  ask('getCompanyInfo', { idCompany: String(companyId) }, `crm:contact-place:${companyId}`).then(
    (data) =>
      (data?.company?.employees ?? []).some(
        (entry) => String(entry.user?._id) !== personId && Number(entry.role) === COMPANY_ADMIN_ROLE && !entry.deactivatedAt,
      ),
    () => false,
  );

/**
 * A contact's page from the api's answer (POST admin/crm/contact/record →
 * { person, companies, activity }), in the shape the page shows. `owner`: the
 * record it was opened from ({ kind, id, name }); `otherAdmins`: by company,
 * whether another administrator remains.
 */
export const contactRecordOf = ({ answer, owner, team = [], otherAdmins = {} }) => {
  const known = answer?.person ?? {};
  const fill = (field) => known[field] || '';
  const id = String(known.id ?? '');
  const account = known.account ?? null;
  const places = (answer?.companies ?? []).map((place) => {
    const kind = place.type === 'group' ? 'group' : 'company';
    // A role is a place of the account (employees); a contact of the company has none.
    const member = kind === 'company' && place.role != null;
    return {
      kind,
      id: String(place.id),
      name: place.name ?? '',
      position: fill('position'),
      role: place.role ?? null,
      contactId: place.contactId ? String(place.contactId) : null,
      account: member ? account : null,
      deactivated: Boolean(place.deactivated),
      otherAdmin: Boolean(otherAdmins[String(place.id)]),
    };
  });
  const ownerPlace = places.find((place) => place.kind === owner.kind && place.id === owner.id);
  // The page's own record is always listed, first, even when the person is no longer at it.
  const memberships = ownerPlace
    ? [ownerPlace, ...places.filter((place) => place !== ownerPlace)]
    : [{ ...owner, position: fill('position'), role: null, contactId: null, account: null, deactivated: false, otherAdmin: false }, ...places];
  const ownerRef = { kind: owner.kind, id: owner.id, name: ownerPlace?.name || owner.name };
  const noteLink = { type: ownerRef.kind, id: ownerRef.id, label: ownerRef.name };
  const activity = answer?.activity ?? {};
  const { history, people } = companyActivitySources(activity, noteLink);
  return {
    person: {
      id,
      kind: known.kind ?? 'email',
      firstname: fill('firstname'),
      lastname: fill('lastname'),
      email: fill('email'),
      phone: fill('phone'),
      position: fill('position'),
      leadStatus: known.leadStatus ?? '',
      account,
      role: ownerPlace?.role ?? null,
      contactIds: (known.contactIds ?? []).map(String),
    },
    owner: ownerRef,
    memberships,
    history: {
      ...history,
      // Its Wildix calls join those logged in the CRM, as on a company's page.
      calls: latestFirst([...history.calls, ...(activity.phoneCalls ?? []).map((call) => ({ ...call, wildix: true, noteLink }))]),
      emails: activity.emails ?? [],
      intercom: 'ok',
    },
    team: peopleOf(Array.isArray(team) ? team : [], people),
  };
};

// The record a contact's page belongs to (its back link, its notes): the one
// named by the payload, else the place of the
// contact sub-document the page is opened on, else the person's first place
// (the api lists an account's companies first). A person at no place has none.
const contactOwnerOf = (payload, places, placeKind) => {
  if (payload.groupId) return { kind: 'group', id: String(payload.groupId) };
  if (payload.companyId) return { kind: 'company', id: String(payload.companyId) };
  const contactId = String(payload.contactId);
  const place = places.find((one) => one.contactId && String(one.contactId) === contactId) ?? places[0];
  return place ? { kind: placeKind(place), id: String(place.id) } : null;
};

/**
 * A contact's page (`{ contactId }`): POST admin/crm/contact/record
 * { contactId }; an id that is not one (a former `hs-<id>` address) is
 * refused 400 INVALID_ID.
 */
const loadContactRecord = async (payload) => {
  const contactId = String(payload.contactId);
  const [answer, team] = await Promise.all([
    ask('getCrmContactRecord', { contactId }),
    ask('getCrmTeam', {}).catch(() => []),
  ]);
  const places = answer?.companies ?? [];
  const placeKind = (place) => (place.type === 'group' ? 'group' : 'company');
  const owner = contactOwnerOf(payload, places, placeKind);
  if (!owner) throw contactNotFound();
  const at = places.find((place) => placeKind(place) === owner.kind && String(place.id) === owner.id);
  const personId = String(answer?.person?.id ?? '');
  const admins = places.filter((place) => placeKind(place) === 'company' && !place.deactivated && Number(place.role) === COMPANY_ADMIN_ROLE);
  const [name, ...kept] = await Promise.all([
    at ? at.name ?? '' : ownerNameOf({ [`${owner.kind}Id`]: owner.id }),
    ...admins.map((place) => keepsAnotherAdmin(place.id, personId)),
  ]);
  const otherAdmins = Object.fromEntries(admins.map((place, index) => [String(place.id), kept[index]]));
  return contactRecordOf({ answer, owner: { ...owner, name }, team, otherAdmins });
};

// A read composed of several calls, in the `(payload, resolve, reject)` shape.
const answered = (load) => (payload, resolve, reject) => load(payload ?? {}).then(resolve, reject);

const crmRecords = {
  getCompanyRecord: answered((payload) => loadCompanyRecord(payload)),

  getGroupRecord: answered(loadGroupRecord),

  // Totals computed by the api for the whole group; an error leaves the tiles empty.
  getGroupInvoiceTotals: (payload, resolve) =>
    ask('getCrmGroupInvoiceTotals', { groupId: String(payload?.groupId) }).then(resolve, () => resolve(null)),

  // A contact's page: the person, where they work, and their activity in all of them (loadContactRecord).
  getContactRecord: answered(loadContactRecord),

  // The companies that may join a group: those in none, found by name.
  listGroupCandidates: answered(async (payload) => {
    const [list, groupOf] = await Promise.all([realCompanies(), bigAccountsById()]);
    const words = (payload.search ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    return list
      .filter((company) => !groupOf[String(company._id)])
      .map((company) => ({ id: String(company._id), name: company.name_commercial || company.reason_social || '', department: company.department ?? '' }))
      .filter((company) => !words || company.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(words))
      .slice(0, 30);
  }),

  // The groups kept a moment (crmRequests.js) are read again after it.
  makeCompanyGroup: (payload, resolve, reject) =>
    ask('makeCrmGroupFromCompany', { companyId: payload.companyId, name: payload.name }).then(
      (answer) => {
        forget('groups');
        resolve({ groupId: answer?.groupId ?? null });
      },
      reject,
    ),

  addCompanyToGroup: (payload, resolve, reject) =>
    ask('linkCompanyToBigAccount', { idCompany: payload.companyId, idBigAccount: payload.groupId }).then((data) => {
      forget('groups');
      resolve(data);
    }, reject),
};

export default crmRecords;
